import React, { useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useLogto } from '@logto/react'
import { isLogtoEnabled } from '@/providers/LogtoProviderWrapper'
import { useIsAuthenticated } from '@/store/useAuthStore'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  // Use Logto auth state if enabled
  if (isLogtoEnabled()) {
    return <LogtoProtectedRoute>{children}</LogtoProtectedRoute>
  }

  // Development mode: use store auth state
  return <DevModeProtectedRoute>{children}</DevModeProtectedRoute>
}

// Logto protected route - use ref to track if user was ever authenticated
// This prevents unmounting children during token refresh
function LogtoProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useLogto()
  const wasAuthenticatedRef = useRef(false)

  // Track if user was ever authenticated in this session
  if (isAuthenticated) {
    wasAuthenticatedRef.current = true
  }

  // If definitely not authenticated (not loading and not authenticated), redirect to landing
  if (!isLoading && !isAuthenticated && !wasAuthenticatedRef.current) {
    return <Navigate to="/" replace />
  }

  // Still loading and never been authenticated - show loading
  if (isLoading && !wasAuthenticatedRef.current) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  // Once authenticated (or was authenticated), keep children mounted
  return <>{children}</>
}

// Development mode protected route
function DevModeProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useIsAuthenticated()

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
